import { Injectable, signal } from '@angular/core';
import { GROUP_USERNAME_CONTEXT } from '@core/interceptors/request.context';
import { CustomerUser, CustomerUserGroup, CustomerUserParam } from '@shared/model/customer.type';
import { PaginateItems } from '@shared/model/paginator.type';
import { map, Observable, switchMap, take, tap } from 'rxjs';
import { BaseApiService } from './base.api';

@Injectable({ providedIn: 'root' })
export class CustomerUserApiService extends BaseApiService {
  private readonly url = 'customersuser';
  private readonly groupUrl = 'customersgroup';
  private readonly context = this.removeContext(GROUP_USERNAME_CONTEXT);

  userGroupList = signal<CustomerUserGroup[]>([]);
  userList = signal<CustomerUser[]>([]);

  getGroups(nextToken: string = null, limit: number = this.PAGE_SIZE): Observable<PaginateItems<CustomerUserGroup>> {
    return this.http
      .post<PaginateItems<CustomerUserGroup>>(this.groupUrl, { action: 'list_groups', nextToken, limit }, this.context)
      .pipe(take(1));
  }

  selectGroup(group_name: string) {
    return this.http.post(this.groupUrl, { action: 'selected_group', group_name }, this.context).pipe(take(1));
  }

  createGroup(group_name: string, sso_group_name: string): Observable<boolean> {
    return this.http
      .post<boolean>(this.groupUrl, { action: 'create_group', group_name, sso_group_name }, this.context)
      .pipe(
        map(this.mapResult),
        switchMap(() => this.createGroupDb(group_name, sso_group_name)),
        take(1),
      );
  }

  private createGroupDb(group_name: string, sso_group_name: string): Observable<boolean> {
    return this.http
      .post<boolean>('customergroupdatabase', { action: 'create_group', group_name, sso_group_name }, this.context)
      .pipe(map(() => true));
  }

  private deleteGroupDb(group_name: string): Observable<boolean> {
    return this.http
      .post<boolean>('customergroupdatabase', { action: 'delete_group', group_name }, this.context)
      .pipe(map(() => true));
  }

  deleteGroup(group_name_to_delete: string): Observable<boolean> {
    return this.http.post<boolean>(this.groupUrl, { action: 'delete_group', group_name_to_delete }, this.context).pipe(
      map(this.mapResult),
      switchMap(() => this.deleteGroupDb(group_name_to_delete)),
      take(1),
    );
  }

  createUser(param: CustomerUserParam): Observable<boolean> {
    return this.http
      .post<boolean>(this.url, { action: 'create_user', ...param }, this.context)
      .pipe(take(1), map(this.mapResult));
  }

  cloneUser(param: CustomerUserParam): Observable<boolean> {
    return this.http
      .post<boolean>(this.url, { action: 'clone_user', ...param }, this.context)
      .pipe(take(1), map(this.mapResult));
  }

  createInternalProvider(user_name: string, group_name: string): Observable<boolean> {
    return this.http
      .post<boolean>(
        this.url,
        { action: 'create_internal_provider_mypbconnect', ...{ group_name, user_name } },
        this.context,
      )
      .pipe(take(1), map(this.mapResult));
  }

  deleteUser(user_name_to_delete: string, group_name: string): Observable<boolean> {
    return this.http
      .post<boolean>(this.url, { action: 'delete_user', ...{ user_name_to_delete, group_name } }, this.context)
      .pipe(take(1), map(this.mapResult));
  }

  getUsers(
    group_name: string,
    nextToken: string = null,
    limit: number = this.PAGE_SIZE,
  ): Observable<PaginateItems<CustomerUser>> {
    return this.http.post<PaginateItems<CustomerUser>>(
      this.url,
      { action: 'list_users', group_name, nextToken, limit },
      this.context,
    );
  }

  loadGroups(): Observable<CustomerUserGroup[]> {
    return this.loadPaginated<CustomerUserGroup>(this.getGroups.bind(this)).pipe(
      tap((groups) => this.userGroupList.set(groups)),
    );
  }

  loadUsers(group_name: string): Observable<CustomerUser[]> {
    return this.loadPaginated<CustomerUser, [string]>(this.getUsers.bind(this), group_name).pipe(
      tap((users) => this.userList.set(users)),
    );
  }

  private mapResult = (result: any) => {
    if (result.status !== true) {
      throw new Error(result.error);
    }
    return result.status;
  };
}
